// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

/**
 * Browser half of reCAPTCHA v3: mint one token for one apply request.
 *
 * Split from `src/lib/recaptcha.ts` so no server verification code — and no
 * reference to the secret key — is reachable from a client bundle. Only
 * `NEXT_PUBLIC_`-prefixed variables are ever inlined into client JavaScript by
 * Next, so the secret could not have leaked either way; keeping the two halves
 * apart makes that structural instead of a thing to remember.
 *
 * The token is minted at SUBMIT time, not on mount: a v3 token expires after
 * about two minutes, and an applicant filling in a long form would otherwise
 * arrive at the server with a stale one and be refused.
 */

/**
 * Multipart part name the token travels under.
 *
 * Both wire constants live in this CLIENT module and are re-exported by the
 * server one, never the other way round: the client needs them, and defining
 * them beside `verifyRecaptcha` would drag the whole verification module into
 * the browser bundle just to reach two strings.
 */
export const RECAPTCHA_FIELD = "recaptchaToken";

/**
 * The action name the client mints its token with.
 *
 * Verified server-side: a token is scoped to the action it was created for, so
 * checking it stops a token harvested from some other form — or some other
 * site of ours — being replayed against the apply endpoint.
 */
export const RECAPTCHA_ACTION = "apply";

/** The URL the v3 script is loaded from, keyed to this site's public key. */
export function recaptchaScriptUrl(siteKey: string): string {
  return `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
}

/** The slice of Google's global this file uses. */
interface Grecaptcha {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

function grecaptcha(): Grecaptcha | null {
  const global = window as unknown as { grecaptcha?: Grecaptcha };
  return global.grecaptcha ?? null;
}

/**
 * Ask Google for a token, or return `null` when the script is not there.
 *
 * `null` rather than a throw: a blocked or slow script is not an exceptional
 * condition, it is a normal thing browsers do, and the caller has to decide
 * what to do about it either way. The server treats a missing token as an
 * unavailable third-party control and retains the rate limit and honeypot.
 */
export async function mintRecaptchaToken(
  siteKey: string,
  action: string
): Promise<string | null> {
  const api = grecaptcha();
  if (!api) return null;

  try {
    // `ready` fires once the library has finished loading. Wrapped in a
    // promise so the caller sees one awaitable rather than a callback.
    await new Promise<void>((resolve) => api.ready(resolve));
    const token = await api.execute(siteKey, { action });
    return token || null;
  } catch {
    // Any failure here is Google's script misbehaving, which we cannot act on
    // and the applicant cannot fix. Treated the same as an absent script.
    return null;
  }
}
